import { HttpErrorResponse } from '@angular/common/http';

import * as Sentry from '@sentry/browser';
import { CaptureContext } from '@sentry/core';

import { jsConfig } from '../config/jsConfig';
import { MonitoringMessageOptions, MonitoringModule } from '../monitoring/monitoring.interface';
import { createSentryMonitoring } from '../monitoring/sentry-monitoring';
import { IGNORED_ERROR_PATTERNS, isFinnairMonitoringUrl } from '../monitoring/scrub';
import { getExtra, getSessionId, normalizeMessage, sanitizeObject } from '../monitoring/error-context';

const SENTRY_ENABLED = !!jsConfig.sentryDsn && typeof window !== 'undefined';

export const captureException = (error: unknown): void => {
  if (SENTRY_ENABLED) {
    Sentry.captureException(error, {
      extra: getExtra(error),
      fingerprint: error instanceof HttpErrorResponse ? [`dedupe-${error.error?.timestamp}`] : undefined,
    });
  }
};

export function captureMessage(message: string, options: MonitoringMessageOptions): void {
  if (SENTRY_ENABLED) {
    const context: CaptureContext = {
      level: options.level,
      extra: getExtra(options.extra || {}),
    };
    Sentry.captureMessage(message, context);
  }
}

export function shouldIncludeUserAgent(userAgent: string): boolean {
  const isOldIe = /MSIE [5678]\./.test(userAgent);
  const isOldAndroid = /Android [234]\.]/.test(userAgent) && !/Chrome/.test(userAgent);
  return !(isOldIe || isOldAndroid);
}

const handleBeforeSend = (event: Sentry.ErrorEvent, hint: Sentry.EventHint): Sentry.ErrorEvent | null => {
  if (!loggingIsAllowedForEvent(event, hint)) {
    return null;
  }

  const fixedRequest = { ...event.request, url: event.request?.url.split('#')[0] };

  const exception = hint?.originalException;
  const isHttpException = exception && exception instanceof HttpErrorResponse;

  const { message, queryParams } = normalizeMessage(isHttpException ? exception : (event.message ?? ''));
  const includeQueryParams = Object.keys(queryParams).length ? { queryParams } : {};
  const fingerprints = event.fingerprint?.filter((f) => !f.startsWith('dedupe-')) ?? [];
  const { __serialized__, ...extra } = event.extra || {};
  const processedEvent: Sentry.ErrorEvent = {
    ...event,
    ...(message ? { message } : {}),
    request: fixedRequest,
    extra: {
      ...extra,
      ...includeQueryParams,
    },
    exception: isHttpException ? undefined : event.exception,
    fingerprint: fingerprints.length > 0 ? fingerprints : undefined,
    tags: {
      ...event.tags,
      'session.id': getSessionId(),
    },
  };
  return sanitizeObject(processedEvent as unknown as Record<string, unknown>) as unknown as Sentry.ErrorEvent; // NOSONAR
};

// This code should be executed in main.ts to ensure Sentry is set up before the application bootstrap
export const initSentry = (): void => {
  if (SENTRY_ENABLED) {
    Sentry.init({
      dsn: jsConfig.sentryDsn,
      release: `${jsConfig.sentryPrefix}${jsConfig.buildEnv}-${jsConfig.versionInfo.releaseGitCommitHash}`,
      environment: jsConfig.sentryEnv,
      sendDefaultPii: true,
      allowUrls: [
        // To get sentry working locally
        /webpack-internal/,
        // MATCH Content-Security-Policy here
        /\.finnair\.com/,
        /\.googletagmanager\.com/,
        /\.google-analytics\.com/,
      ],
      normalizeDepth: 4,
      ignoreErrors: IGNORED_ERROR_PATTERNS,
      beforeSend: handleBeforeSend,
    });
  }
};

const getStatus = (event: Sentry.Event, hint: Sentry.EventHint): number | undefined => {
  const extraStatus = event?.extra?.status;
  if (typeof extraStatus === 'number') {
    return extraStatus;
  }
  const errorStatus = (event?.extra?.error as HttpErrorResponse)?.status;
  const hintStatus = (hint?.originalException as HttpErrorResponse)?.status;
  return errorStatus ?? hintStatus;
};

export function loggingIsAllowedForEvent(event: Sentry.Event, hint: Sentry.EventHint): boolean {
  if (!SENTRY_ENABLED) {
    return false;
  }

  if (!shouldIncludeUserAgent(window.navigator.userAgent)) {
    return false;
  }

  if (event.request?.url && !isSentryAllowedForDomain(event.request.url)) {
    return false;
  }

  const status = getStatus(event, hint);

  // Filter out http 0 errors
  return status !== 0;
}

export const isSentryAllowedForDomain = isFinnairMonitoringUrl;

/** The Sentry (browser) monitoring backend — one child of the client {@link MonitoringAdapter}. */
export const sentryClientMonitoring: MonitoringModule = createSentryMonitoring({
  init: initSentry,
  captureException,
  captureMessage,
  addBreadcrumb: (name, context) =>
    Sentry.addBreadcrumb({ category: 'action', level: 'info', message: name, data: context }),
});
